/**
 * Admin navigation: single source of truth for admin tab bar.
 *
 * Tab bar shows main operational sections:
 * Главная | Заказы | Платежи | Мероприятия
 *
 * Full sections list (including "coming soon") is in AdminSectionsGrid.tsx
 */

import { hasOperatorAccess, hasAdminAccess } from "@/lib/admin-permissions";

export type AdminSectionPermission = "operator" | "admin";

/** Icon types available for admin navigation */
export type AdminNavIcon = 
  | "home" 
  | "payments" 
  | "orders" 
  | "events" 
  | "tickets"
  | "homeGroups"
  | "ministries"
  | "broadcasts"
  | "content"
  | "analytics"
  | "settings";

export interface AdminNavItem {
  id: string;
  path: string;
  pathEnd?: boolean;
  label: string;
  /** operator = payments.verify OR admin.full; admin = admin.full only */
  permission: AdminSectionPermission;
  icon: AdminNavIcon;
}

/**
 * Main tab bar items (bottom navigation).
 * Only shows active, operational sections.
 * Future sections are shown in AdminSectionsGrid on the home page.
 */
export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    id: "home",
    path: "/admin",
    pathEnd: true,
    label: "Главная",
    permission: "operator",
    icon: "home",
  },
  {
    id: "orders",
    path: "/admin/orders",
    label: "Заказы",
    pathEnd: false,
    permission: "operator",
    icon: "orders",
  },
  {
    id: "payments",
    path: "/admin/payments",
    label: "Платежи",
    permission: "operator",
    icon: "payments",
  },
  {
    id: "events",
    path: "/admin/events",
    label: "События",
    permission: "admin",
    icon: "events",
  },
];

/**
 * Подпункт блока «Заказы» на главной админки: выдача билетов за наличные.
 * Доступ: operator (payments.verify | admin.full).
 * Отдельный путь для только выдачи билетов: /admin/tickets/cash (tickets.issue | admin.full).
 */
export const ADMIN_ORDERS_CASH_ISSUE = {
  id: "orders-cash-issue",
  path: "/admin/orders/cash-issue",
  label: "Выдать билеты за наличные",
  permission: "operator" as AdminSectionPermission,
  icon: "orders" as const,
};

export interface AccountForAdminNav {
  permissions?: string[];
}

function canAccessSection(
  item: AdminNavItem | typeof ADMIN_ORDERS_CASH_ISSUE,
  account: AccountForAdminNav | null
): boolean {
  const permissions = account?.permissions;
  if (item.permission === "operator") return hasOperatorAccess(permissions);
  return hasAdminAccess(permissions);
}

/**
 * Returns admin nav items (tabs) visible to the account.
 */
export function getVisibleAdminNavItems(
  account: AccountForAdminNav | null
): AdminNavItem[] {
  return ADMIN_NAV_ITEMS.filter((item) => canAccessSection(item, account));
}

/**
 * Returns items for section "Заказы и билеты" on admin home: Заказы + Выдать билеты за наличные (by permission).
 */
export function getOrdersSectionItems(account: AccountForAdminNav | null): Array<{ id: string; path: string; label: string; icon: "orders" }> {
  if (!hasOperatorAccess(account?.permissions)) return [];
  const items: Array<{ id: string; path: string; label: string; icon: "orders" }> = [
    { id: "orders", path: "/admin/orders", label: "Заказы", icon: "orders" },
    { id: ADMIN_ORDERS_CASH_ISSUE.id, path: ADMIN_ORDERS_CASH_ISSUE.path, label: ADMIN_ORDERS_CASH_ISSUE.label, icon: "orders" },
  ];
  return items;
}

/**
 * First path the account is allowed to access (for redirect from /admin).
 */
export function getFirstAllowedAdminPath(
  account: AccountForAdminNav | null
): string {
  const visible = getVisibleAdminNavItems(account);
  if (visible.length === 0) return "/admin/payments";
  return visible[0].path;
}
